import PropTypes from 'prop-types';
import { createContext, useReducer, useEffect, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';
import { openSnackbar } from 'store/reducers/snackbar';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import Loader from 'components/Loader';
import axios, { axiosV2 } from 'utils/axios';
import { setUserRoles } from 'store/reducers/authSlice';


const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user_id: '',
  project: '',
  user: {}
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);
  return decoded.exp > Date.now() / 1000; // Check token expiration
};

// auth/get-user-permissions/ now nests modules under office_types (e.g. "Front Office",
// "Visitor Office") instead of returning a flat `modules` array — same restructuring as
// auth/list-user-permissions/. The menu (Navigation/index.js) and PermissionGuard both expect one
// flat [{module_name, permissions: [...]}] array regardless of which office type granted it, so
// merge every office type's modules together here (falls back to the old flat `modules` shape if
// office_types isn't present, so this keeps working either way).
const flattenPermissionsResponse = (data) => {
  const officeTypes = data?.office_types;
  if (!Array.isArray(officeTypes)) {
    return data?.modules || [];
  }
  const mergedByModule = {};
  officeTypes.forEach((office) => {
    (office.modules || []).forEach((mod) => {
      if (!mergedByModule[mod.module_name]) {
        mergedByModule[mod.module_name] = { module_name: mod.module_name, permissions: [] };
      }
      const seen = new Set(mergedByModule[mod.module_name].permissions.map((perm) => perm.name));
      (mod.permissions || []).forEach((perm) => {
        if (!seen.has(perm.name)) {
          mergedByModule[mod.module_name].permissions.push(perm);
          seen.add(perm.name);
        }
      });
    });
  });
  return Object.values(mergedByModule);
};

let myTimeout = null;
let myInterval = null;

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const reduxDispatch = useDispatch(); // rename useDispatch to avoid conflict
  const [user, setUser] = useState({});

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = localStorage.getItem('serviceToken');
        const role = localStorage.getItem('role');
        const roleId = localStorage.getItem('roleId');
        const username = localStorage.getItem('username');
        const refreshtoken = localStorage.getItem('refreshtoken');

        if (serviceToken && refreshtoken && verifyToken(serviceToken)) {
          setSession(serviceToken, role, roleId, username, refreshtoken, dispatch, navigate);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user_id: roleId,
              user: user
            }
          });
        } else if (serviceToken && refreshtoken) {
          // Access token has expired (e.g. the tab was backgrounded or the laptop was asleep long
          // enough that the periodic refresh never got a chance to run) — try refreshing before
          // giving up, since the refresh token itself may still be perfectly valid. Previously this
          // logged the user out immediately without even attempting that.
          await RefreshTokenCall(dispatch, navigate);
          const refreshedToken = localStorage.getItem('serviceToken');
          if (refreshedToken && verifyToken(refreshedToken)) {
            setSession(refreshedToken, role, roleId, username, localStorage.getItem('refreshtoken'), dispatch, navigate);
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user_id: roleId,
                user: user
              }
            });
          }
          // else: RefreshTokenCall already dispatched LOGOUT and navigated to /vms/login on failure.
        } else {
          dispatch({ type: LOGOUT });
        }
      } catch (err) {
        console.error(err);
        dispatch({ type: LOGOUT });
      }
    };
    init();

    // Cleanup on unmount
    return () => {
      if (myTimeout) {
        clearTimeout(myTimeout);
        myTimeout = null;
      }
      if (myInterval) {
        clearInterval(myInterval);
        myInterval = null;
      }
    };
  }, [user, dispatch, navigate]);

  // const RefreshTokenCall = async (dispatch, navigate) => {
  //   try {
  //     const refreshtoken = localStorage.getItem('refreshtoken');
  //     if (refreshtoken && refreshtoken.length > 0) {
  //       // const response = await axios.post('/user/custom-refresh-token/', {
  //       //   refresh: refreshtoken
  //       // });
  //       const response = await axios.post('user/refresh-token/', {
  //         refresh_token: refreshtoken
  //       });
  //       if (response.data.response_code === 200) {
  //         console.log('inside if', response.data.data)
  //         // const { access, refresh } = response.data.data;
  //         const { access_token } = response.data.data;
  //         // Update tokens in localStorage and axios headers
  //         // localStorage.setItem('serviceToken', access);
  //         // localStorage.setItem('refreshtoken', refresh);
  //         localStorage.setItem('refreshtoken', access_token);
  //         axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
  //       }
  //       else if (response.data.response_code !== 200) {
  //         console.log('inside else if block')
  //         const errorMessage = response.data.message;
  //         dispatch({ type: LOGOUT });
  //         localStorage.clear();
  //         reduxDispatch(
  //           openSnackbar({
  //             open: true,
  //             message: errorMessage || 'Refresh token request failed',
  //             variant: 'alert',
  //             alert: { color: 'error' },
  //             close: false
  //           })
  //         );
  //         navigate('/vms/login', { replace: true });
  //       }
  //     } else {
  //       console.log('inside else block')
  //       dispatch({ type: LOGOUT });
  //       localStorage.clear();
  //       reduxDispatch(
  //         openSnackbar({
  //           open: true,
  //           message: 'Failed to refresh token. Please log in again.',
  //           variant: 'alert',
  //           alert: { color: 'error' },
  //           close: false
  //         })
  //       );
  //       navigate('/vms/login', { replace: true });
  //     }


  //     } catch (error) {
  //       console.log('inside catch block')
  //       dispatch({ type: LOGOUT });
  //       localStorage.clear();
  //       reduxDispatch(
  //         openSnackbar({
  //           open: true,
  //           message: error.response.data.message || 'Failed to refresh token. Please log in again.',
  //           variant: 'alert',
  //           alert: { color: 'error' },
  //           close: false
  //         })
  //       );
  //       navigate('/vms/login', { replace: true });
  //     }

  //   };


  // catch (error) {
  //   console.log('inside catch block', error);

  //   const errorMessage =
  //     (error.response && error.response.data && error.response.data.message) ||
  //     'Failed to refresh token. Please log in again.';

  //   dispatch({ type: LOGOUT });
  //   localStorage.clear();
  //   reduxDispatch(
  //     openSnackbar({
  //       open: true,
  //       message: errorMessage,
  //       variant: 'alert',
  //       alert: { color: 'error' },
  //       close: false
  //     })
  //   );
  //   navigate('/vms/login', { replace: true });
  // }

  const RefreshTokenCall = async (dispatch, navigate) => {
    try {
      const refreshtoken = localStorage.getItem('refreshtoken');

      if (refreshtoken && refreshtoken.length > 0) {
        const response = await axios.post('user/refresh-token/', {
          refresh_token: refreshtoken
        });

        console.log('refresh token response', response?.data);

        if (response?.data?.response_code === 200) {
          const { access_token, refresh_token: rotatedRefreshToken } = response.data;

          // Store the new access token (overwriting old refresh token as per your existing logic)
          localStorage.setItem('serviceToken', access_token);
          // If the backend rotates refresh tokens (issues a new one-time-use token on every
          // refresh), the old one in localStorage would silently stop working on the next cycle
          // unless this is saved too — persist it whenever the response includes one.
          if (rotatedRefreshToken) {
            localStorage.setItem('refreshtoken', rotatedRefreshToken);
          }
          // Update axios headers
          axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        } else {
          const errorMessage = response?.data?.message || 'Refresh token request failed';
          dispatch({ type: LOGOUT });
          localStorage.clear();
          reduxDispatch(
            openSnackbar({
              open: true,
              message: errorMessage,
              variant: 'alert',
              alert: { color: 'error' },
              close: false
            })
          );
          navigate('/vms/login', { replace: true });
        }
      } else {
        dispatch({ type: LOGOUT });
        localStorage.clear();
        reduxDispatch(
          openSnackbar({
            open: true,
            message: 'Failed to refresh token. Please log in again.',
            variant: 'alert',
            alert: { color: 'error' },
            close: false
          })
        );
        navigate('/vms/login', { replace: true });
      }
    } catch (error) {
      // No `error.response` means the request never actually reached the server (offline, DNS
      // hiccup, a slow connection timing out) — that's not proof the session is invalid, so don't
      // nuke it. Only a real response from the server (401/400/etc, meaning it explicitly rejected
      // the refresh token) means the session is actually done and the user needs to log in again.
      // Previously ANY failure here — including a single dropped request — force-logged the user
      // out, which is the likely cause of unexpected periodic logouts.
      if (!error?.response) {
        console.error('Refresh token request failed to reach the server — will retry next cycle:', error);
        return;
      }
      console.error('Refresh token error:', error);
      dispatch({ type: LOGOUT });
      localStorage.clear();
      reduxDispatch(
        openSnackbar({
          open: true,
          message: error?.response?.data?.message || 'Failed to refresh token. Please log in again.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
      navigate('/vms/login', { replace: true });
    }
  };

  // Start the initial timeout for 5 minutes (300,000 ms)
  const startRefreshTokenTimeout = (dispatch, navigate) => {
    return setTimeout(() => {
      RefreshTokenCall(dispatch, navigate);
      // After the first refresh, switch to setInterval to run every 5 minutes
      myInterval = setInterval(() => RefreshTokenCall(dispatch, navigate), 5 * 60 * 1000);
      myTimeout = null; // Reset timeout as the interval will take over
    }, 5 * 60 * 1000); // Delay of 5 minutes
  };

  const setSession = (serviceToken, role, roleId, username, refreshtoken, dispatch, navigate) => {
    if (serviceToken) {
      localStorage.setItem('serviceToken', serviceToken);
      localStorage.setItem('role', role);
      localStorage.setItem('roleId', roleId);
      localStorage.setItem('username', username);
      localStorage.setItem('refreshtoken', refreshtoken);
      axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

      // If no timeout or interval exists, set the timeout for the first refresh
      if (!myTimeout && !myInterval) {
        myTimeout = startRefreshTokenTimeout(dispatch, navigate);
      }
    } else {
      // Clear any existing timeout/interval if the user logs out
      if (myTimeout) clearTimeout(myTimeout);
      if (myInterval) clearInterval(myInterval);
      localStorage.clear();
    }
  };

  const generateOTP = async (values) => {
    try {
      const response = await axios.post(
        'user/generate-otp/',
        {
          phone_no: values.Phone_no
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return error;
    }
  };

  const login = async (number, values) => {
    const phone_no = number;
    const OTP = values;
    try {
      const response = await axios.post(
        'user/validate-otp/',
        {
          phone_no: phone_no,
          otp: OTP
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      console.log('resonse login', response?.data)
      if (response.data.response_code === 200) {
        const { data } = response;
        setUser(data);
        const { access_token, role, user_id, name, refresh_token, projects, office_types } = data;
        setSession(access_token, role, user_id, name, refresh_token, dispatch);
        const projectName = projects?.[0]?.project_name;
        if (projectName) localStorage.setItem('project_name', projectName);
        if (Array.isArray(projects) && projects.length > 0) {
          localStorage.setItem('project_names', JSON.stringify(projects.map((p) => p.project_name).filter(Boolean)));
        }
        // Persisted so dashboard routing (see isFrontOfficeOnly in MiniDrawerStyled.js) can tell a
        // Front-Office-only role apart from anything else WITHOUT hardcoding that role's name —
        // unlike the flattened permissions below, which merge every office type together and lose
        // this distinction entirely.
        localStorage.setItem('officeTypeNames', JSON.stringify((office_types || []).map((o) => o.office_type_name).filter(Boolean)));
        verifyToken(access_token);
        const permRes = await axiosV2.get('auth/get-user-permissions/',
          {
            headers: {
              Authorization: `Bearer ${access_token}`
            }
          }
        );
        const permissions = flattenPermissionsResponse(permRes?.data);
        console.log('permissions for redux', permissions);
        reduxDispatch(setUserRoles(permissions));
        // Ensure correct structure and dispatch after fetching
        if (Array.isArray(permissions) && permissions.length > 0) {
          reduxDispatch(setUserRoles(permissions));
        } else {
          console.log('No permissions received from API');
          reduxDispatch(setUserRoles([]));
        }
        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user_id: data.user_id,
            user: data
          }
        });


        // Every role lands on the same dashboard now — which sections it actually shows is
        // decided by permissions (see pages_vms/dashboard_vms/UnifiedDashboard.jsx), not by role.
        navigate('/vms/dashboard/');
        return response;
      }
      if (response.data.response_code !== 200) {
        return response;
      }
      handleLoginError(response);
    } catch (error) {
      dispatch({ type: LOGOUT });
      handleErrorSnack(error.message);
    }
  };

  const handleLoginError = (response) => {
    const errorMessage = response.data.message;
    reduxDispatch(
      openSnackbar({
        open: true,
        message: errorMessage,
        variant: 'alert',
        alert: { color: 'error' },
        close: false
      })
    );
  };

  const handleErrorSnack = (message) => {
    reduxDispatch(
      openSnackbar({
        open: true,
        message: message,
        variant: 'alert',
        alert: { color: 'error' },
        close: false
      })
    );
  };

  const logout = async () => {
    const serviceToken = localStorage.getItem('serviceToken');
    const refreshtoken = localStorage.getItem('refreshtoken');
    try {
      const response = await axios.post(
        'logout/',
        { refresh_token: refreshtoken },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${serviceToken}`
          }
        }
      );
      if (response.data.response_code === 200) {
        handleSuccessfulLogout();
      } else {
        handleLoginError(response);
      }
    } catch (error) {
      handleErrorSnack('Internal server error');
    }
  };

  const handleSuccessfulLogout = () => {
    setSession(null, null, null, null, null, dispatch);
    localStorage.clear();
    // Also clear per-page list filters (search/date-range/pagination) saved during this browser
    // session — otherwise the next login (an in-app route change, not a real page reload) would
    // restore the previous user's stale filters instead of defaulting fresh.
    sessionStorage.clear();
    dispatch({ type: LOGOUT });
    // navigate('/vms/login');
    navigate('/');
    reduxDispatch(
      openSnackbar({
        open: true,
        message: 'Logout Success',
        variant: 'alert',
        alert: { color: 'success' },
        close: false
      })
    );
  };

  if (!state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, logout, generateOTP, user }}>{children}</JWTContext.Provider>;
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
